import { useEffect, useRef, type ReactNode } from "react";
import { useAuth } from "@/shared/auth/useAuth";
import { useSession } from "@/shared/auth/SessionContext";
import { OperatorDesktopBlockScreen } from "@/shared/components/session/OperatorDesktopBlockScreen";
import { SessionTerminatedOverlay } from "@/shared/components/session/SessionTerminatedOverlay";

/**
 * SessionGate wraps protected routes. It:
 * 1. Auto-initializes a session when the user authenticates
 * 2. Restores the session on page refresh (localStorage — shared across tabs)
 * 3. Shows OperatorDesktopBlockScreen for operator on desktop
 * 4. Shows SessionTerminatedOverlay when the session was killed
 * 5. Fails open: if the session service errors, the user is let through
 *
 * V2: No more SessionConflictModal — auto-kick handles session limits.
 */
export function SessionGate({ children }: { children: ReactNode }) {
  const { user, licenseType, isLoading: authLoading } = useAuth();
  const {
    sessionCheck,
    isSessionActive,
    isInitializing,
    isTerminated,
    initializeSession,
    logout,
  } = useSession();

  const initializedRef = useRef(false);

  // Auto-initialize when user is available and session isn't active yet
  useEffect(() => {
    if (user && licenseType && !isSessionActive && !isInitializing && !initializedRef.current) {
      initializedRef.current = true;
      initializeSession();
    }
    // Reset the ref when user logs out
    if (!user) {
      initializedRef.current = false;
    }
  }, [user, licenseType, isSessionActive, isInitializing, initializeSession]);

  // While auth is loading, show nothing (AuthGuard handles its own spinner)
  if (authLoading) return null;

  // If no user, children handle redirect via AuthGuard
  if (!user) return <>{children}</>;

  // Show terminated overlay (blocking — no escape)
  if (isTerminated) {
    return <SessionTerminatedOverlay />;
  }

  // Session check returned a denial — only operator desktop block remains
  if (sessionCheck && !sessionCheck.allowed) {
    if (
      sessionCheck.reason === "operator_desktop_blocked" ||
      sessionCheck.reason === "operator_web_blocked"
    ) {
      return <OperatorDesktopBlockScreen />;
    }
  }

  // Suppress unused variable warning — logout is used by SessionTerminatedOverlay via context
  void logout;

  // Session is active or initializing (fail open) — render children
  return <>{children}</>;
}
